import { relations, sql } from 'drizzle-orm'
import { check, decimal, pgTable, text, uniqueIndex } from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getDateField,
  getNullableIdReference,
} from '../common/utils'
import { teams } from './team'

// Monthly revenue and sales targets, and the two hand-entries the management
// report needs where the model cannot know better. A null teamId is the
// company-wide row; a team row carries that team's slice.
//
// Both hand-entries are rates, not amounts: they compose with the capacity
// identity (days × utilisation × €/day) instead of replacing its result, so a
// roster change still moves the figure. The note is what makes them auditable,
// so the report can show why a figure was not calculated.
export const monthlyTargets = pgTable(
  'monthly_targets',
  {
    ...getBaseFields(),
    // First day of the month the targets apply to.
    month: getDateField().notNull(),
    teamId: getNullableIdReference(() => teams.id),
    revenueTarget: decimal({ precision: 12, scale: 2 }),
    // Left empty when the sales target should be derived from the revenue
    // target and the measured sales-to-delivery lag.
    salesTarget: decimal({ precision: 12, scale: 2 }),
    // Replaces the measured €/työpäivä for this team and month.
    eurPerDayOverride: decimal({ precision: 10, scale: 2 }),
    // Planned headcount change for this month, in person-months: 1.0 is one
    // person for the whole month, 0.5 someone starting mid-month. Kept here
    // rather than as an employment row, because a termination is not written
    // against a person until it has actually happened, and a planned hire has
    // nobody to write it against yet.
    fteDelta: decimal({ precision: 5, scale: 2 }),
    overrideNote: text(),
  },
  (table) => [
    ...getBaseIndexes('monthly_targets', table),
    // Two partial indexes rather than one over (month, teamId): Postgres treats
    // NULLs as distinct, so a plain unique index would let several company-wide
    // rows share a month.
    uniqueIndex('monthly_targets_month_team_idx')
      .on(table.month, table.teamId)
      .where(sql`deleted_at IS NULL AND team_id IS NOT NULL`),
    uniqueIndex('monthly_targets_month_company_idx')
      .on(table.month)
      .where(sql`deleted_at IS NULL AND team_id IS NULL`),
    // Enforced here rather than in the upsert schema so no caller can store a
    // bare override: an unexplained hand-entered figure is indistinguishable
    // from a measured one once it is on the report.
    check(
      'monthly_targets_override_needs_note',
      sql`(${table.eurPerDayOverride} IS NULL AND ${table.fteDelta} IS NULL) OR btrim(coalesce(${table.overrideNote}, '')) <> ''`,
    ),
  ],
)

export const monthlyTargetsRelations = relations(monthlyTargets, ({ one }) => ({
  team: one(teams, {
    fields: [monthlyTargets.teamId],
    references: [teams.id],
  }),
}))
